import { useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";

import { maxLookbackDaysForTimeGranularity } from "@features/sales-v3/utils/max-lookback-days-for-time-granularity";
import {
  FloatingPortal,
  flip,
  safePolygon,
  shift,
  useDelayGroup,
  useFloating,
  useHover,
  useInteractions,
  type Placement,
} from "@floating-ui/react";
import { constants, type ITimeGranularityV2 } from "@north-beam/nb-common";
import { DateRangeCalendar } from "@shared/date-range-calendar";

interface Props {
  compareDateRange: constants.CompareDateRange | null;
  timeGranularity: ITimeGranularityV2;
  placement?: Placement;
  selectCustomDateRange({
    startDate,
    endDate,
  }: {
    startDate: string;
    endDate: string;
  }): void;
}

export const CompareCustomDateRangeSelect = ({
  compareDateRange,
  timeGranularity,
  placement,
  selectCustomDateRange,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const [, maxLookbackDaysCompare] =
    maxLookbackDaysForTimeGranularity(timeGranularity);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    transform: false,
    placement: placement || "right-start",
    middleware: [flip(), shift()],
  });

  const { delay } = useDelayGroup(context);

  const hover = useHover(context, {
    delay,
    handleClose: safePolygon({
      requireIntent: false,
      blockPointerEvents: true,
      buffer: 1,
    }),
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const startDate =
    compareDateRange &&
    constants.isCompareDateRangeOfType(compareDateRange, "custom")
      ? compareDateRange.startDate
      : null;
  const endDate =
    compareDateRange &&
    constants.isCompareDateRangeOfType(compareDateRange, "custom")
      ? compareDateRange.endDate
      : null;

  return (
    <div>
      <div
        className={clsx(
          "hover:cursor-pointer hover:bg-cosmic-4 hover:font-semibold px-4 py-1.5 flex",
          "items-center justify-between",
          { "bg-cosmic-3 font-semibold": isOpen },
        )}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <div>
          <i className="fa-regular fa-sharp fa-calendar text-cosmic text-xs mr-2" />
          <span className="text-interstellar text-sm leading-loose">
            Custom
          </span>
        </div>
        <i className="fa-regular fa-sharp fa-chevron-right text-cosmic text-xs" />
      </div>
      <FloatingPortal>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              transition={{ ease: "easeOut", duration: 0.2 }}
              initial={{ opacity: 0, translateY: "0.25rem" }}
              animate={{ opacity: 1, translateY: 0 }}
              exit={{ opacity: 0, translateY: "0.25rem" }}
              className="z-[2004] nb-date-picker"
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <DateRangeCalendar
                className="bg-white rounded-lg elevation-1"
                maxLookbackDays={
                  timeGranularity === "hourly"
                    ? maxLookbackDaysCompare
                    : undefined
                }
                value={{ startDate, endDate }}
                onApply={selectCustomDateRange}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </FloatingPortal>
    </div>
  );
};
